import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  subscriptionsService,
  Subscription,
  BillingInfo,
} from '@/services/subscriptions';
import { useFeatures } from '@/hooks/useFeatures';
import { useTenantProfile } from '@/hooks/useTenant';
import { useAuthStore } from '@/context/authStore';
import Button from '@/components/ui/Button';
import {
  CreditCard,
  CheckCircle2,
  AlertCircle,
  Clock,
  XCircle,
  Loader2,
  Copy,
  MessageCircle,
  Send,
  Ban,
  ExternalLink,
  Building2,
} from 'lucide-react';
import { formatPrice, formatDate } from '@/utils/format';
import toast from 'react-hot-toast';

const STATUS_LABELS: Record<string, { label: string; color: string; icon: any }> = {
  trial: { label: 'Prueba', color: 'bg-blue-100 text-blue-700', icon: Clock },
  active: { label: 'Activa', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  suspended: { label: 'Suspendida', color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
  cancelled: { label: 'Cancelada', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const PAYMENT_LABELS: Record<string, { label: string; color: string }> = {
  paid: { label: 'Al día', color: 'bg-green-100 text-green-700' },
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' },
  overdue: { label: 'Vencida', color: 'bg-red-100 text-red-700' },
};

function copyToClipboard(text: string, label: string) {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copiado`);
}

export default function Billing() {
  const { user, tenant: authTenant } = useAuthStore();
  const { data: tenantProfile } = useTenantProfile();
  const tenant = tenantProfile || authTenant; // Priorizamos el tenant del endpoint (siempre fresco)
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const { data: features } = useFeatures();
  const mpEnabled = features?.mp_enabled === true;

  const { data: subscription, isLoading, error } = useQuery<Subscription>({
    queryKey: ['subscription', 'me'],
    queryFn: subscriptionsService.getMine,
    retry: false,
  });

  const { data: billingInfo } = useQuery<BillingInfo>({
    queryKey: ['billing-info'],
    queryFn: subscriptionsService.getBillingInfo,
    retry: false,
    staleTime: 0,          // Siempre consultar de nuevo al entrar
    refetchOnMount: 'always',
  });

  const [reference, setReference] = useState('');
  const [showReportModal, setShowReportModal] = useState(false);

  const reportMutation = useMutation({
    mutationFn: () => subscriptionsService.reportPayment(reference.trim() || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription', 'me'] });
      toast.success('¡Gracias! Confirmaremos la recepción del pago en breve.');
      setShowReportModal(false);
      // Abrir app nativa de WhatsApp automaticamente si hay numero configurado.
      // Nota: navegar (no window.open) para que Chrome/Edge invoquen el protocol handler
      // y la app de WhatsApp Desktop lo capture.
      if (waLink) {
        window.location.href = waLink;
      }
      setReference('');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.detail || 'No se pudo enviar el aviso');
    },
  });

  // MP legacy (oculto si feature flag off)
  const activateMutation = useMutation({
    mutationFn: subscriptionsService.activate,
    onSuccess: (data) => {
      window.location.href = data.init_point;
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.detail || 'No se pudo iniciar la activación');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: subscriptionsService.cancel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription', 'me'] });
      toast.success('Suscripción cancelada');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.detail || 'No se pudo cancelar');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-taupe-600" />
      </div>
    );
  }

  if (error || !subscription) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="h-12 w-12 text-taupe-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-taupe-800 mb-2">Sin suscripción asignada</h2>
        <p className="text-taupe-600">Tu almacén todavía no tiene una suscripción. Contactá al soporte.</p>
      </div>
    );
  }

  const statusInfo = STATUS_LABELS[subscription.status] || STATUS_LABELS.trial;
  const paymentInfo = PAYMENT_LABELS[subscription.payment_status] || PAYMENT_LABELS.pending;
  const StatusIcon = statusInfo.icon;

  const hasPaymentReported = !!subscription.payment_reported_at;
  const isPaid = subscription.payment_status === 'paid';
  const isOverdue = subscription.payment_status === 'overdue';

  // Preparar mensaje de WhatsApp. Usamos el esquema `whatsapp://` para abrir la app nativa
  // en vez de WhatsApp Web. Si no esta instalada, el browser cae al fallback wa.me.
  const tenantName = tenant?.name || 'mi almacén';
  const dueDateStr = subscription.next_payment_date
    ? ` con vencimiento el ${formatDate(subscription.next_payment_date)}`
    : '';
  const todayStr = new Date().toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const waMessage = `Hola! Soy ${user?.full_name || ''} del almacén "${tenantName}". Realicé la transferencia de $${subscription.price_monthly} el ${todayStr}${dueDateStr}, correspondiente a mi suscripción mensual de ANIMAstore${reference ? ` (Ref: ${reference})` : ''}.`;
  const waPhone = billingInfo?.whatsapp_number?.replace(/\D/g, '') || null;
  // Esquema nativo (abre la app de WhatsApp Desktop / Mobile directamente)
  const waLink = waPhone ? `whatsapp://send?phone=${waPhone}&text=${encodeURIComponent(waMessage)}` : null;
  // Fallback web (por si no tiene la app instalada)
  const waLinkWeb = waPhone ? `https://wa.me/${waPhone}?text=${encodeURIComponent(waMessage)}` : null;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Facturación</h1>
        <p className="text-gray-600">Gestioná el pago de tu suscripción</p>
      </div>

      {/* Banner según estado */}
      {isOverdue && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-900">Pago vencido</p>
            <p className="text-sm text-red-700">Tu suscripción tiene un pago pendiente. Para seguir usando ANIMAstore, realizá la transferencia lo antes posible.</p>
          </div>
        </div>
      )}

      {hasPaymentReported && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
          <Clock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-blue-900">Pago reportado</p>
            <p className="text-sm text-blue-700">
              Avisaste que pagaste el {formatDate(subscription.payment_reported_at!)}.
              Estamos verificando la recepción.
            </p>
          </div>
        </div>
      )}

      {/* Card principal */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-sm text-gray-500 uppercase tracking-wide">Plan actual</p>
            <h2 className="text-2xl font-bold text-gray-900 capitalize">{subscription.plan}</h2>
            <p className="text-3xl font-bold text-taupe-700 mt-2">
              {formatPrice(subscription.price_monthly)}
              <span className="text-base text-gray-500 font-normal">/mes</span>
            </p>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
              <StatusIcon className="h-3.5 w-3.5" />
              {statusInfo.label}
            </span>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${paymentInfo.color}`}>
              Pago: {paymentInfo.label}
            </span>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-gray-100">
          <div>
            <p className="text-xs text-gray-500">Último pago</p>
            <p className="text-sm font-medium text-gray-900">
              {subscription.last_payment_date ? formatDate(subscription.last_payment_date) : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Próximo vencimiento</p>
            <p className="text-sm font-medium text-gray-900">
              {subscription.next_payment_date ? formatDate(subscription.next_payment_date) : '—'}
            </p>
          </div>
        </div>
      </div>

      {/* Card de transferencia bancaria + reporte de pago (siempre visible) */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
            <Building2 className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Datos para transferencia</h3>
            <p className="text-sm text-gray-500">
              {isPaid
                ? 'Podés adelantar el próximo pago si querés'
                : 'Pagá tu suscripción transfiriendo a estos datos'}
            </p>
          </div>
        </div>

        {isPaid && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-900">
            <p className="font-medium">Tu suscripción está al día ✅</p>
            {subscription.next_payment_date && (
              <p className="mt-1">
                Próximo vencimiento: <strong>{formatDate(subscription.next_payment_date)}</strong>. Podés adelantar el pago ahora si querés.
              </p>
            )}
          </div>
        )}

          {billingInfo && (billingInfo.alias || billingInfo.cbu) ? (
            <div className="space-y-3">
              {billingInfo.alias && (
                <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Alias</p>
                    <p className="font-mono font-medium text-gray-900">{billingInfo.alias}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(billingInfo.alias!, 'Alias')}
                    className="p-2 rounded-lg hover:bg-gray-200 text-gray-600"
                    title="Copiar"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              )}
              {billingInfo.cbu && (
                <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">CBU</p>
                    <p className="font-mono font-medium text-gray-900 break-all">{billingInfo.cbu}</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(billingInfo.cbu!, 'CBU')}
                    className="p-2 rounded-lg hover:bg-gray-200 text-gray-600 flex-shrink-0"
                    title="Copiar"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              )}
              {billingInfo.account_holder && (
                <div className="bg-gray-50 rounded-lg px-4 py-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Titular</p>
                  <p className="font-medium text-gray-900">{billingInfo.account_holder}</p>
                </div>
              )}
              {billingInfo.bank_name && (
                <div className="bg-gray-50 rounded-lg px-4 py-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Banco</p>
                  <p className="font-medium text-gray-900">{billingInfo.bank_name}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 mb-3">
              <p className="text-sm text-yellow-900">
                Los datos bancarios aún no están cargados. Contactá al administrador del sistema.
              </p>
            </div>
          )}

          {billingInfo?.payment_instructions && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-900 whitespace-pre-line">{billingInfo.payment_instructions}</p>
            </div>
          )}

          {isAdmin && (
            <div className="mt-6 flex flex-wrap gap-3">
              {!hasPaymentReported && (
                <Button
                  onClick={() => setShowReportModal(true)}
                  className="bg-taupe-700 hover:bg-taupe-800 text-cream-50"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Ya pagué
                </Button>
              )}
              {waLink && (
                <>
                  <a href={waLink}>
                    <Button className="bg-green-50 hover:bg-green-100 text-green-700 border border-green-200">
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Avisar por WhatsApp (app)
                    </Button>
                  </a>
                  {waLinkWeb && (
                    <a href={waLinkWeb} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 self-center underline">
                      abrir en web
                    </a>
                  )}
                </>
              )}
            </div>
          )}
      </div>

      {/* Card MP (solo si feature flag ON) */}
      {mpEnabled && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Mercado Pago (automático)</h3>
              <p className="text-sm text-gray-500">Pago automático con tarjeta mes a mes</p>
            </div>
          </div>
          {subscription.mp_status === 'authorized' ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <p className="font-medium text-green-900">Suscripción activa en Mercado Pago</p>
              <p className="text-sm text-green-700 mt-1">
                Próximo cobro automático: {subscription.next_payment_date ? formatDate(subscription.next_payment_date) : '—'}
              </p>
            </div>
          ) : subscription.mp_status === 'pending' && subscription.mp_init_point ? (
            <a
              href={subscription.mp_init_point}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-900"
            >
              Continuar activación en Mercado Pago
              <ExternalLink className="h-4 w-4" />
            </a>
          ) : isAdmin ? (
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => activateMutation.mutate()}
                disabled={activateMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {activateMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
                Activar con Mercado Pago
              </Button>
            </div>
          ) : null}
          {subscription.mp_status === 'authorized' && isAdmin && (
            <Button
              onClick={() => {
                if (confirm('¿Cancelar el cobro automático en MP?')) cancelMutation.mutate();
              }}
              disabled={cancelMutation.isPending}
              className="mt-3 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200"
            >
              <Ban className="h-4 w-4 mr-2" />
              Cancelar MP
            </Button>
          )}
        </div>
      )}

      {/* Modal "Ya transferí" */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowReportModal(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Reportar pago</h3>
            <p className="text-sm text-gray-500 mb-4">
              Avisanos que ya realizaste la transferencia. Confirmaremos la recepción en breve.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Número de comprobante (opcional)
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Ej: 12345678"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none"
            />
            <div className="flex gap-3 justify-end mt-6">
              <Button
                onClick={() => setShowReportModal(false)}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => reportMutation.mutate()}
                disabled={reportMutation.isPending}
                className="bg-taupe-700 hover:bg-taupe-800 text-cream-50"
              >
                {reportMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Enviar aviso
              </Button>
            </div>
          </div>
        </div>
      )}

      {subscription.notes && (
        <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Notas</p>
          <p className="text-sm text-gray-700 whitespace-pre-line">{subscription.notes}</p>
        </div>
      )}
    </div>
  );
}
